package com.shadowings.gero.presentation.component.quiz

import androidx.compose.animation.BoundsTransform
import androidx.compose.animation.ExperimentalSharedTransitionApi
import androidx.compose.animation.SharedTransitionLayout
import androidx.compose.animation.animateContentSize
import androidx.compose.animation.core.MutableTransitionState
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.LocalMinimumInteractiveComponentSize
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.rememberGraphicsLayer
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.LessonIds
import com.shadowings.gero.domain.model.QuizQuestionType
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.model.WordSelectionContent
import com.shadowings.gero.domain.model.WrongAnswerRecord
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.model.HeartActions
import com.shadowings.gero.presentation.model.QuizUiState
import com.shadowings.gero.presentation.screen.QuizScreenContent
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.quizInputCorrectBg
import com.shadowings.gero.presentation.ui.quizInputCorrectBorder
import com.shadowings.gero.presentation.ui.quizInputWrongBg
import com.shadowings.gero.presentation.ui.quizInputWrongBorder
import com.shadowings.gero.presentation.ui.testModeCorrect
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.question_type_meaning
import gero.composeapp.generated.resources.quiz_ripasso_traslitterazione
import gero.composeapp.generated.resources.quiz_show_options_button
import gero.composeapp.generated.resources.share_quiz_word
import gero.composeapp.generated.resources.word_bank_object_plural
import gero.composeapp.generated.resources.word_bank_object_singular
import gero.composeapp.generated.resources.word_bank_subject_plural
import gero.composeapp.generated.resources.word_bank_subject_singular
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Preview(name = "Landscape", widthDp = 800, heightDp = 360, showBackground = true)
@Composable
private fun WordSelectionComposablePreview() {
    AppTheme {
        Surface {
            QuizScreenContent(
                uiState = QuizUiState(
                    lessonId = LessonIds.INTRO_HIEROGLYPHS,
                    quizList = listOf(
                        WordSelectionContent(
                            id = "preview",
                            hieroglyphs = "W19-M17-G43-E13-O34h0.5:X1*B1",
                            correctPhrase = "the cat of the woman",
                            words = "Lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua Ut enim ad minim veniam, quis nostrud exercitation Ut enim ad minim veniam, quis nostrud exercitation".split(
                                " "
                            )
                        )
                    ),
                    userData = UserData()
                ),
                hiero = listOf(),
                onQuizCompleted = { _, _, _ -> },
                onAddExperience = {},
                heartActions = HeartActions({}),
                navController = rememberNavController()
            )
        }
    }
}

data class UISelectedWord(
    val index: Int,
    val transitionState: MutableTransitionState<Boolean>
)

// The answer row's chips (uiSelectedWords) are only an animation buffer on top of
// logicalSelectedIndices, which is what actually gets graded: they also hold chips still
// fading out after removal. Their visible (targetState = true) entries must always mirror
// logicalSelectedIndices, in the same order — any drift means the user sees one sentence and
// is graded on another. These two helpers are the only ways entries enter the buffer.

// Rebuilds the buffer from the (rememberSaveable-restored) logical selection, so after a
// rotation/Activity recreation the answer row shows what will be graded instead of going empty.
internal fun uiSelectedWordsFrom(indices: List<Int>): List<UISelectedWord> =
    indices.map { UISelectedWord(it, MutableTransitionState(true)) }

// A chip re-selected while still fading out is moved to the end, since logicalSelectedIndices
// appends it at the end too; reviving it in its old slot would desync the displayed order.
internal fun MutableList<UISelectedWord>.showSelected(index: Int) {
    val existing = find { it.index == index }
    if (existing != null) remove(existing)
    val entry = existing ?: UISelectedWord(index, MutableTransitionState(false))
    entry.transitionState.targetState = true
    add(entry)
}

@OptIn(ExperimentalSharedTransitionApi::class)
@Composable
fun WordSelectionComposable(
    quiz: WordSelectionContent,
    comboCount: MutableState<Int>,
    quizList: MutableState<List<IQuizContent>>,
    page: Int,
    pagerState: PagerState,
    navController: NavController,
    quizResults: MutableState<Map<String, Boolean>>,
    errorsCount: MutableState<Int>,
    testMode: Boolean,
    userData: UserData,
    lessonId: String = "",
    hideOptions: Boolean = false,
    onComboFiveReached: (() -> Unit)? = null,
    onNextQuestion: ((isCorrect: Boolean) -> Unit)? = null,
    onAnswerResult: ((isCorrect: Boolean, record: WrongAnswerRecord) -> Unit)? = null
) {
    val state = rememberQuizPageState(
        quizId = quiz.id,
        page = page,
        pagerState = pagerState,
        navController = navController,
        quizResults = quizResults,
        quizList = quizList,
        comboCount = comboCount,
        errorsCount = errorsCount,
        onComboFiveReached = onComboFiveReached,
        onNextQuestion = onNextQuestion,
    )
    val graphicsLayer = rememberGraphicsLayer()

    val coroutineScope = rememberCoroutineScope()
    var revealed by rememberSaveable(quiz.id) { mutableStateOf(false) }
    var logicalSelectedIndices by rememberSaveable(quiz.id, stateSaver = IntListSaver) {
        mutableStateOf(emptyList<Int>())
    }
    val uiSelectedWords = remember(quiz.id) {
        mutableStateListOf(*uiSelectedWordsFrom(logicalSelectedIndices).toTypedArray())
    }
    val selectedWords = remember(logicalSelectedIndices, quiz.words) {
        logicalSelectedIndices.map { quiz.words[it] }
    }
    val isRtl = remember { kotlin.random.Random.nextBoolean() }

    val correctWordIndices = remember(quiz.correctPhrase, quiz.words) {
        buildCorrectWordSelectionIndices(
            words = quiz.words,
            correctPhrase = quiz.correctPhrase
        )
    }
    val testModeCorrectWordIndices = if (testMode) correctWordIndices else emptyList()
    val deviceInfo = rememberDeviceInfo()
    SharedTransitionLayout {
        val sharedTransitionScope = this
        // Word-bank chips need to pack tightly to fit many words on screen, so we opt out
        // of Compose's default invisible touch-target padding (which would otherwise force
        // every chip up to 48dp and wreck the layout density). WordChip/WordPlaceholder
        // compensate with an explicit 44dp minimum visual height instead.
        CompositionLocalProvider(
            LocalMinimumInteractiveComponentSize provides 0.dp
        ) {
            Box(modifier = Modifier.fillMaxSize()) {
                val questionContent: @Composable ColumnScope.() -> Unit = {
                    val questionTitle =
                        if (quiz.soggettiCount != null && quiz.oggettiCount != null) {
                            val subjectsText = if (quiz.soggettiCount == 1) {
                                stringResource(Res.string.word_bank_subject_singular)
                            } else {
                                stringResource(
                                    Res.string.word_bank_subject_plural,
                                    quiz.soggettiCount
                                )
                            }
                            val objectsText = if (quiz.oggettiCount == 1) {
                                stringResource(Res.string.word_bank_object_singular)
                            } else {
                                stringResource(
                                    Res.string.word_bank_object_plural,
                                    quiz.oggettiCount
                                )
                            }
                            stringResource(Res.string.question_type_meaning) + " ($subjectsText, $objectsText)"
                        } else {
                            stringResource(Res.string.question_type_meaning)
                        }

                    val questionSize = when (deviceInfo.deviceClass) {
                        DeviceClass.PHONE_SMALL -> 48.dp
                        DeviceClass.PHONE_MEDIUM -> 72.dp
                        else -> 80.dp
                    }

                    val questionPadding = when (deviceInfo.deviceClass) {
                        DeviceClass.PHONE_SMALL -> 0.dp
                        DeviceClass.PHONE_MEDIUM -> 4.dp
                        else -> 8.dp
                    }

                    QuestionBox(
                        question = questionTitle,
                        modifier = Modifier.padding(vertical = questionPadding),
                        questionTestTag = "word_selection_question",
                        wordContent = {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .drawWithContent {
                                        graphicsLayer.record {
                                            this@drawWithContent.drawContent()
                                        }
                                        drawContent()
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                MdCWord(
                                    word = quiz.hieroglyphs,
                                    rtl = isRtl,
                                    cart = false,
                                    size = questionSize,
                                    testTag = quiz.hieroglyphs
                                )
                            }
                        }
                    )
                }

                val optionsContent: @Composable ColumnScope.() -> Unit = {
                    // The selected words row (the "empty line")
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            //.background(Color.Blue)
                            .heightIn(min = 48.dp)
                            .padding(horizontal = 24.dp)
                            .semantics { testTag = "word_selection_selected_row" },
                        contentAlignment = Alignment.TopStart
                    ) {
                        // A nice horizontal line at the bottom representing the empty row placeholder
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(2.dp)
                                .background(
                                    color = MaterialTheme.colorScheme.outline.copy(alpha = 0.3f),
                                    shape = RoundedCornerShape(1.dp)
                                )
                                .align(Alignment.BottomCenter)
                        )

                        // Display selected words horizontally with FlowRow
                        FlowRow(
                            modifier = Modifier
                                .fillMaxWidth()
                                .align(Alignment.TopStart)
                                .padding(bottom = 8.dp)
                                .animateContentSize(),
                            horizontalArrangement = Arrangement.spacedBy(4.dp, Alignment.Start),
                            verticalArrangement = Arrangement.spacedBy(2.dp, Alignment.Top)
                        ) {
                            uiSelectedWords.forEach { uiWord ->
                                val index = uiWord.index
                                key(index) {
                                    val word = quiz.words[index]
                                    val borderCol = when {
                                        state.showResult && state.correct -> quizInputCorrectBorder
                                        state.showResult && !state.correct -> quizInputWrongBorder
                                        else -> MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
                                    }
                                    val bgCol = when {
                                        state.showResult && state.correct -> quizInputCorrectBg
                                        state.showResult && !state.correct -> quizInputWrongBg
                                        else -> MaterialTheme.colorScheme.surface
                                    }
                                    androidx.compose.animation.AnimatedVisibility(
                                        visibleState = uiWord.transitionState,
                                        enter = fadeIn(animationSpec = tween(200)),
                                        exit = fadeOut(animationSpec = tween(200))
                                    ) {
                                        WordChip(
                                            word = word,
                                            border = BorderStroke(2.dp, borderCol),
                                            containerColor = bgCol,
                                            onClick = {
                                                if (state.pending) {
                                                    logicalSelectedIndices =
                                                        logicalSelectedIndices.filter { it != index }
                                                    uiWord.transitionState.targetState = false
                                                    val uiWordToRemove = uiWord
                                                    coroutineScope.launch {
                                                        delay(200)
                                                        if (!uiWordToRemove.transitionState.targetState) {
                                                            uiSelectedWords.remove(
                                                                uiWordToRemove
                                                            )
                                                        }
                                                    }
                                                }
                                            },
                                            enabled = state.pending,
                                            modifier = with(sharedTransitionScope) {
                                                Modifier.sharedElement(
                                                    sharedContentState = rememberSharedContentState(
                                                        key = "word_$index"
                                                    ),
                                                    animatedVisibilityScope = this@AnimatedVisibility,
                                                    boundsTransform = BoundsTransform { _, _ ->
                                                        tween(durationMillis = 200)
                                                    }
                                                )
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(16.dp))

                    if (hideOptions && !revealed) {
                    RevealOptionsButton(
                        modifier = Modifier.weight(1f),
                        onClick = { revealed = true }
                    )
                    } else {
                    // Word Bank of remaining words — fills the available space and scrolls
                    // vertically when there are more options than fit (small phones, landscape).
                    FlowRow(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxWidth()
                            //.background(Color.Green)
                            .verticalScroll(rememberScrollState())
                            .semantics { testTag = "word_selection_word_bank" },
                        horizontalArrangement = Arrangement.spacedBy(
                            2.dp,
                            Alignment.CenterHorizontally
                        ),
                        verticalArrangement = Arrangement.spacedBy(2.dp, Alignment.Top)
                    ) {
                        quiz.words.forEachIndexed { index, word ->
                            Box(
                                contentAlignment = Alignment.Center
                            ) {
                                // Always render placeholder to occupy space
                                WordPlaceholder(word = word)

                                androidx.compose.animation.AnimatedVisibility(
                                    visible = index !in logicalSelectedIndices,
                                    enter = fadeIn(animationSpec = tween(200)),
                                    exit = fadeOut(animationSpec = tween(200))
                                ) {
                                    val isCorrectWord = index in testModeCorrectWordIndices
                                    val testModeCardBorder = if (isCorrectWord) {
                                        BorderStroke(2.dp, testModeCorrect)
                                    } else {
                                        BorderStroke(
                                            2.dp,
                                            MaterialTheme.colorScheme.outline.copy(alpha = 0.3f)
                                        )
                                    }

                                    WordChip(
                                        word = word,
                                        onClick = {
                                            if (state.pending && index !in logicalSelectedIndices) {
                                                logicalSelectedIndices =
                                                    logicalSelectedIndices + index
                                                uiSelectedWords.showSelected(index)
                                            }
                                        },
                                        enabled = state.pending && index !in logicalSelectedIndices,
                                        border = if (testMode) testModeCardBorder else BorderStroke(
                                            2.dp,
                                            MaterialTheme.colorScheme.outline.copy(alpha = 0.3f)
                                        ),
                                        modifier = with(sharedTransitionScope) {
                                            Modifier.sharedElement(
                                                sharedContentState = rememberSharedContentState(
                                                    key = "word_$index"
                                                ),
                                                animatedVisibilityScope = this@AnimatedVisibility,
                                                boundsTransform = BoundsTransform { _, _ ->
                                                    tween(durationMillis = 200)
                                                }
                                            )
                                        }
                                    )
                                }
                            }
                        }
                    }
                    }
                }

                QuizContentScaffold(
                    testTag = "word_selection_content",
                    isLandscape = deviceInfo.isLandscape,
                    landscapeQuestionArrangement = Arrangement.SpaceEvenly,
                    landscapeQuestionFillHeight = true,
                    landscapeQuestionRowAlignment = Alignment.Top,
                    landscapeOptionsWeight = 1.2f,
                    landscapeOptionsArrangement = Arrangement.SpaceBetween,
                    landscapeOptionsHorizontalAlignment = Alignment.CenterHorizontally,
                    landscapeOptionsHorizontalPadding = 0.dp,
                    landscapeOptionsScrollable = false,
                    landscapeOptionsRowAlignment = Alignment.Bottom,
                    portraitOptionsArrangement = Arrangement.spacedBy(0.dp),
                    portraitOptionsScrollable = false,
                    portraitOptionsHorizontalPadding = 0.dp,
                    portraitSpacerBeforeOptions = 8.dp,
                    questionContent = questionContent,
                    optionsContent = optionsContent
                )

                val quizInfo = """
                    Quiz ID: ${quiz.id}
                    Type: Word Selection Quiz
                    Hieroglyphs: ${quiz.hieroglyphs}
                    Words options: ${quiz.words.joinToString(", ")}
                    Correct Phrase: ${quiz.correctPhrase}
                """.trimIndent()
                val userAnswer = selectedWords.joinToString(" ")

                val shareText = stringResource(Res.string.share_quiz_word)

                QuizBottomFeedbackAndButton(
                    modifier = Modifier.align(Alignment.BottomCenter),
                    state = state,
                    buttonEnabled = selectedWords.isNotEmpty(),
                    info = quiz.transliteration?.let {
                        stringResource(
                            Res.string.quiz_ripasso_traslitterazione,
                            it
                        )
                    },
                    correctAnswer = quiz.correctPhrase,
                    userData = userData,
                    lessonId = lessonId,
                    quizInfo = quizInfo,
                    userAnswer = userAnswer,
                    shareText = shareText,
                    shareImageProvider = {
                        try {
                            graphicsLayer.toImageBitmap()
                        } catch (e: Exception) {
                            null
                        }
                    },
                    onConfirmClick = {
                        val answer = selectedWords.joinToString(" ")
                        val isCorrect = (listOf(quiz.correctPhrase) + quiz.alternativePhrases).any { phrase ->
                            isTranslationMatch(answer, phrase) || normalizePhrase(answer) == normalizePhrase(phrase)
                        }
                        onAnswerResult?.invoke(
                            isCorrect,
                            WrongAnswerRecord(
                                id = WrongAnswerRecord.buildId(lessonId, quiz.hieroglyphs),
                                lessonId = lessonId,
                                type = QuizQuestionType.WORD_SELECTION,
                                questionText = quiz.transliteration ?: quiz.hieroglyphs,
                                correctAnswerText = quiz.correctPhrase,
                                userAnswerText = userAnswer,
                                hieroglyphs = quiz.hieroglyphs,
                                options = quiz.words,
                                alternativeAnswers = quiz.alternativePhrases,
                                subjectsCount = quiz.soggettiCount,
                                objectsCount = quiz.oggettiCount
                            )
                        )
                        isCorrect
                    }
                )
            }
        }
    }
}

@Composable
private fun RevealOptionsButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val hapticFeedback = LocalHapticFeedback.current
    Box(
        modifier = modifier.fillMaxWidth().padding(vertical = 24.dp),
        contentAlignment = Alignment.Center
    ) {
        Button(
            onClick = {
                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                onClick()
            }
        ) {
            Text(text = stringResource(Res.string.quiz_show_options_button))
        }
    }
}

@Composable
private fun WordPlaceholder(
    word: String,
    modifier: Modifier = Modifier
) {
    val deviceInfo = rememberDeviceInfo()
    // Must match WordChip's padding exactly: this placeholder reserves the
    // space in the word bank, so any mismatch makes real chips shrink/grow
    // relative to it when they fade in/out, shifting neighboring words.
    val padding = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 2.dp
        DeviceClass.PHONE_MEDIUM -> 2.dp
        else -> 4.dp
    }
    OutlinedCard(
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.outlinedCardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.05f),
            contentColor = Color.Transparent,
            disabledContainerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.05f),
            disabledContentColor = Color.Transparent
        ),
        border = BorderStroke(2.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.1f)),
        modifier = modifier
            .padding(padding)
            .heightIn(min = 44.dp)
    ) {
        Box(
            modifier = Modifier
                .heightIn(min = 44.dp)
                .padding(horizontal = 6.dp + padding, vertical = 4.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = word,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Medium,
                color = Color.Transparent,
                textAlign = TextAlign.Center
            )
        }
    }
}

@Composable
private fun WordChip(
    word: String,
    onClick: () -> Unit,
    enabled: Boolean,
    modifier: Modifier = Modifier,
    border: BorderStroke = BorderStroke(2.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f)),
    containerColor: Color = MaterialTheme.colorScheme.surface,
    tag: String = word
) {
    val deviceInfo = rememberDeviceInfo()
    val hapticFeedback = LocalHapticFeedback.current
    val padding = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 2.dp
        DeviceClass.PHONE_MEDIUM -> 2.dp
        else -> 4.dp
    }
    // ...existing code...
    OutlinedCard(
        onClick = {
            hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
            onClick()
        },
        enabled = enabled,
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.outlinedCardColors(
            containerColor = containerColor,
            contentColor = MaterialTheme.colorScheme.onSurface,
            disabledContainerColor = containerColor,
            disabledContentColor = MaterialTheme.colorScheme.onSurface
        ),
        border = border,
        modifier = modifier
            .padding(padding)
            .heightIn(min = 44.dp)
            .semantics { testTag = tag }
    ) {
        Box(
            modifier = Modifier
                .heightIn(min = 44.dp)
                .padding(horizontal = 6.dp + padding, vertical = 4.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = word,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Medium,
                textAlign = TextAlign.Center
            )
        }
    }
}

internal fun buildCorrectWordSelectionIndices(
    words: List<String>,
    correctPhrase: String
): List<Int> {
    val phraseTokens = tokenizeCorrectPhrase(correctPhrase)
    val usedIndices = mutableSetOf<Int>()
    val result = mutableListOf<Int>()

    phraseTokens.forEach { token ->
        val matchIndex = words.indices.firstOrNull { index ->
            index !in usedIndices && normalizeToken(words[index]) == normalizeToken(token)
        } ?: return@forEach

        usedIndices += matchIndex
        result += matchIndex
    }

    return result
}

internal fun tokenizeCorrectPhrase(correctPhrase: String): List<String> {
    return correctPhrase
        .replace("’", "'")
        .replace("'", "' ")
        .replace(" ,", ",")
        .trim()
        .split(Regex("\\s+"))
        .flatMap { token ->
            when {
                token.isBlank() -> emptyList()
                token.endsWith(",") && token.length > 1 -> listOf(token.dropLast(1), ",")
                else -> listOf(token)
            }
        }
}

internal fun normalizePhrase(s: String): String {
    return tokenizeCorrectPhrase(s).joinToString(" ") { normalizeToken(it) }
}

internal fun normalizeToken(token: String): String {
    return token.replace("’", "'")
        .replace(" '", "'")
        .replace("' ", "'")
        .trim()
        .lowercase()
}
